import cloneDeep from "lodash/cloneDeep";
import isArray from "lodash/isArray";
import isNil from "lodash/isNil";
import slice from "lodash/slice";
import { FormFieldData } from "PFApp/types/activity_form/activity_form_schema";
import { activityCustomTypesAccessLevels } from "PFCore/helpers/custom_types";
import {
  AccessLevel,
  ActivityPureCustomValue,
  CheckboxesFieldValue,
  CurrentProfile,
  CustomType,
  CustomValueState,
  Id,
  ParsedSchemaProperty,
  Profile,
  PureCustomValue
} from "PFCore/types";

import { ActivityWithDefaultAttributes } from "./activity_form_default_attributes.types";

export const getActivityWithoutBlockedCustomValues = (
  activity: ActivityWithDefaultAttributes
): ActivityWithDefaultAttributes => {
  const activityCopy: ActivityWithDefaultAttributes = cloneDeep(activity);

  // eslint-disable-next-line camelcase
  activityCopy.custom_fields = activityCopy.custom_fields?.map(({ values, ...customField }) => ({
    ...customField,
    values: values.filter(({ state }) => state !== CustomValueState.Blocked)
  }));

  return activityCopy;
};

type FillPropertyWithDefaultValueParams = {
  property: ParsedSchemaProperty;
  fieldsLinkedFromEngagement: FormFieldData[];
  subtemplateDefaultValue: PureCustomValue[] | string | CheckboxesFieldValue | null;
  customTypes: CustomType[];
  currentProfile: CurrentProfile;
  activityCreator: Profile | null;
};

export const fillPropertyWithDefaultValue = (
  result: ActivityWithDefaultAttributes,
  {
    property,
    fieldsLinkedFromEngagement,
    subtemplateDefaultValue,
    customTypes,
    currentProfile,
    activityCreator
  }: FillPropertyWithDefaultValueParams
) => {
  const linkedFromEngagementValue = fieldsLinkedFromEngagement.find(
    (field) => field.fieldName === property.name
  )?.values;
  const defaultPropertyValue = linkedFromEngagementValue || subtemplateDefaultValue || property.default;

  const defaultValue =
    property.kind === "single" && isArray(defaultPropertyValue)
      ? slice(defaultPropertyValue as any[], 0, 1)
      : defaultPropertyValue;

  if (!defaultValue) {
    return;
  }

  if (property.name === "booking_category_id") {
    const valueBlank = isNil(result.booking_category_id);
    if (valueBlank) {
      result.booking_category_id = defaultValue as Id;
    }

    return;
  }

  if (property.type !== "custom_field") {
    const valueBlank = isNil(result.metadata![property.name]);
    if (valueBlank) {
      result.metadata![property.name] = defaultValue;
    }

    return;
  }

  const customType = customTypes.filter((type) => type.name === property.name)[0];
  const valueBlank = !result.custom_fields!.find((field) => field.type.id === customType.id);

  const accessLevel = activityCustomTypesAccessLevels(currentProfile, activityCreator)[customType.id];
  const isWritePermitted = accessLevel === AccessLevel.ReadWrite;
  const isReadOnly = accessLevel === AccessLevel.ReadOnly;
  const isLinkedFromEngagement = !isNil(linkedFromEngagementValue);
  const bypassReadPermission = isReadOnly && isLinkedFromEngagement;

  if (customType && defaultValue && valueBlank && (isWritePermitted || bypassReadPermission)) {
    result.custom_fields = [
      ...result.custom_fields!,
      {
        type: { id: customType.id as number, name: customType.name },
        values: defaultValue as ActivityPureCustomValue[]
      }
    ];
  }
};
